/**
 * CF-V11-CTA-MORPH-V1: BoostTierSelector
 *
 * BOOST + MEGA cards beside each other, same visual treatment as HEADS/TAILS
 * (rounded dark, gold border + checkmark when selected). Tapping the selected
 * tier reverts to 'standard'; tapping the other tier switches mutually
 * exclusive.
 *
 * Per Scorer R4 SIGN binding 9: tier_config comes from the backend
 * /api/v1/balance response — never from local constants. The percent and rate
 * labels render from the validated tierConfig payload only.
 *
 *   multiplier_bps → percent extra over Standard
 *     22000 (Boost) → (22000-20000)/100 = +20%
 *     25000 (Mega)  → (25000-20000)/100 = +50%
 *
 *   fee_bps → per-TON rate label (PL #5881)
 *     1500 (Boost) → 0.15 / TON
 *     3500 (Mega)  → 0.35 / TON
 *
 * Absolute scaled cost shows in the FLIP button via deriveCtaCopy at commit
 * moment — this card only shows the per-TON rate so it stays stable across
 * chip selections (PL #5882).
 */

import type { BoostTier, TierConfigPayload, TierConfigEntry } from '../lib/boost-tier-config';
import { findTierEntry } from '../lib/ui-derivers';
import { useT } from '../i18n';

interface BoostTierSelectorProps {
    selectedTier: BoostTier;
    onChange: (tier: BoostTier) => void;
    tierConfig: TierConfigPayload;
    disabled?: boolean;
}

const STANDARD_MULT_BPS = 20000;
const NANO_PER_TON = 1_000_000_000n;

function percentExtra(multBps: number): string {
    return `+${Math.round((multBps - STANDARD_MULT_BPS) / 100)}%`;
}

/**
 * Display the fee as fractional TON per stake TON.
 *  fee_bps 1500 → fee per 1 TON = 1500 / 10000 = 0.15 → "0.15"
 * Computed via BigInt to keep it consistent with backend math discipline.
 */
function feePerTonLabel(feeBps: number): string {
    const stakeOneTonNano = NANO_PER_TON;
    const feeNano = (stakeOneTonNano * BigInt(feeBps)) / 10000n;
    const whole = feeNano / NANO_PER_TON;
    const frac = feeNano % NANO_PER_TON;
    if (frac === 0n) return whole.toString();
    const fracStr = frac.toString().padStart(9, '0').slice(0, 2);
    const trimmed = fracStr.replace(/0+$/, '');
    return trimmed.length === 0 ? whole.toString() : `${whole}.${trimmed}`;
}

interface TierCardProps {
    tier: 'boost' | 'mega';
    entry: TierConfigEntry;
    selected: boolean;
    disabled: boolean;
    onClick: () => void;
}

function TierCard({ tier, entry, selected, disabled, onClick }: TierCardProps) {
    const t = useT();
    const prefix = tier === 'boost' ? '⚡️' : '⚡️⚡️';
    // CF-DRAGONTIGER-ZH-V1: the tier label is resolved from the tier IDENTITY
    // ('boost'/'mega') via t('tier.*') — NOT the backend entry.label — so zh renders
    // the themed names (黄金 / 钻石 per Scorer ruling 1144 / PL #8395; supersedes the
    // now-stale keep-Latin note in cf-zh-keytable _meta for tier identity labels).
    // The backend entry.* payload still drives pct + fee — numbers stay backend-sourced.
    const tierLabel = t('tier.' + tier);
    const pct = Math.round((entry.multiplier_bps - STANDARD_MULT_BPS) / 100);
    const ariaLabel = selected
        ? t('boost.tier_selected_aria', { label: tierLabel })
        : t('boost.tier_select_aria', { label: tierLabel, pct, fee: feePerTonLabel(entry.fee_bps) });
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-pressed={selected}
            aria-label={ariaLabel}
            className={`choice-card relative flex-1 rounded-2xl py-3.5 flex flex-col items-center gap-1.5 transition-all ${selected ? 'selected' : ''} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
        >
            {selected && (
                <span
                    className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-black"
                    style={{
                        background: 'linear-gradient(180deg, var(--gold-light), var(--gold-dark))',
                        color: '#1c1917',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                    }}
                    aria-hidden="true"
                >
                    ✓
                </span>
            )}
            <div
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 dt-tier-disc dt-tier-disc-${tier}`}
                style={{
                    background: selected
                        ? 'linear-gradient(145deg, #fcd34d 0%, #d4a017 100%)'
                        : 'var(--bg-elevated)',
                    boxShadow: selected ? '0 8px 30px rgba(244,213,111,0.25)' : 'none',
                }}
            >
                <span className="text-xl dt-tier-glyph" aria-hidden="true">{prefix}</span>
            </div>
            <span
                className="text-[12px] font-bold tracking-wide"
                style={{ color: selected ? 'var(--gold)' : 'var(--text-secondary)' }}
            >
                {t('boost.tier_label_pct', { LABEL: tierLabel.toUpperCase(), pct })}
            </span>
            <span
                className="text-[10px] font-medium"
                style={{ color: 'var(--text-muted)' }}
            >
                {t('boost.tier_fee_per_ton', { fee: feePerTonLabel(entry.fee_bps) })}
            </span>
        </button>
    );
}

export function BoostTierSelector({ selectedTier, onChange, tierConfig, disabled = false }: BoostTierSelectorProps) {
    const boost = findTierEntry(tierConfig, 'boost');
    const mega = findTierEntry(tierConfig, 'mega');

    // Fail-closed: if the backend tier_config is missing either non-Standard
    // tier, render nothing. parseBalanceDto (App.tsx) already enforces all 3
    // tier keys must be present — this is a defensive secondary check that
    // would only fire if the contract drifted.
    if (!boost || !mega) {
        return null;
    }

    const handleTierClick = (tier: 'boost' | 'mega') => {
        if (disabled) return;
        onChange(selectedTier === tier ? 'standard' : tier);
    };

    return (
        <div className="flex gap-3 mb-4" style={{ fontVariantNumeric: 'tabular-nums' }}>
            <TierCard
                tier="boost"
                entry={boost}
                selected={selectedTier === 'boost'}
                disabled={disabled}
                onClick={() => handleTierClick('boost')}
            />
            <TierCard
                tier="mega"
                entry={mega}
                selected={selectedTier === 'mega'}
                disabled={disabled}
                onClick={() => handleTierClick('mega')}
            />
        </div>
    );
}
